import React from "react";
import {
  BaseSite,
  DIALOG_FLAG_SHOW_CLOSE,
  DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN,
  DIALOG_FLAG_SHOW_DIALOG_IMMERSIVE
} from "../../shared/BaseSite";
import {BaseApp} from "../../shared/BaseApp";
import {StyledBoxColumn} from "../../shared/StyledComponents";
import {Swiper, SwiperSlide} from "swiper/react";
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import {Autoplay, Pagination} from "swiper/modules";
import {MimeTypeMap} from "../../shared/mime_types";

export interface GalleryCustomRenderer {

  isMimetypeSupported(mimeType: string): boolean;

  render(url: string): React.ReactNode;
}

export class GalleryHelper {

  private static sCustomRenderers: GalleryCustomRenderer[] = [];

  static registerCustomRenderer(renderer: GalleryCustomRenderer): void {
    this.sCustomRenderers.push(renderer);
  }

  static getCustomRenderer(mimeType: string): GalleryCustomRenderer | undefined {
    return this.sCustomRenderers.find(renderer => renderer.isMimetypeSupported(mimeType));
  }

  static showGalleryDialog(urls: string[], selectedIndex?: number): void {
    const context = BaseSite.CONTEXT || BaseApp.CONTEXT;
    context.showDialog(
      {flags: DIALOG_FLAG_SHOW_CLOSE | DIALOG_FLAG_SHOW_DIALOG_FULLSCREEN | DIALOG_FLAG_SHOW_DIALOG_IMMERSIVE},
      () => <GalleryFragment urls={urls} selectedIndex={selectedIndex}/>
    );
  }
}

type GalleryFragmentProps = {
  urls: string[],
  selectedIndex?: number,
}

function GalleryFragment(props: GalleryFragmentProps) {
  let selectedIndex: number = props.selectedIndex || 0;
  const ext = MimeTypeMap.getFileExtensionFromUrl(props.urls[selectedIndex]);
  const map = MimeTypeMap.getInstance();
  const mimetype = map.getMimeTypeFromExtension(ext);
  let urls: string[];
  // Displays images as swipeable pages, but only the selected media item for other mimetypes.
  if (MimeTypeMap.isImage(mimetype)) {
    urls = props.urls.filter(url => MimeTypeMap.isImage(map.getMimeTypeFromExtension(MimeTypeMap.getFileExtensionFromUrl(url))));
    selectedIndex = urls.findIndex(url => url === props.urls[selectedIndex]);
  } else {
    urls = [props.urls[selectedIndex]];
    selectedIndex = 0;
  }
  const customRenderer = GalleryHelper.getCustomRenderer(mimetype);
  return <StyledBoxColumn
    style={{
      display: "flex",
      flexDirection: "column",
      height: "100%",
      alignItems: "center",
      justifyContent: "center"
    }}>
    {Boolean(customRenderer) ? customRenderer.render(urls[0]) : null}
    {mimetype === MimeTypeMap.MimeType_application_pdf ? <GalleryFragmentRenderPdf url={urls[0]}/> : null}
    {MimeTypeMap.isImage(mimetype) || urls.length > 1 ?
      <GalleryFragmentRenderImages urls={urls} selectedIndex={selectedIndex}/> : null}
  </StyledBoxColumn>;
}

function GalleryFragmentRenderImages({urls, selectedIndex}: { urls: string[], selectedIndex?: number }) {
  return <Swiper
    initialSlide={selectedIndex}
    style={{width: "100%", aspectRatio: 4 / 3, height: "100%", maxHeight: "92dvh"}}
    // autoplay={{
    //   delay: 1000, // Time between transitions in milliseconds (default 3000)
    //   disableOnInteraction: false, // Continue autoplay even after user interaction (default true)
    // }}
    pagination={true}
    modules={[Pagination, Autoplay]}>
    {urls?.map(url => <SwiperSlide>
      <img src={url} style={{
        verticalAlign: "top",
        width: "100%",
        height: "100%",
        objectFit: "contain",
      }}/>
    </SwiperSlide>)}
  </Swiper>;
}

function GalleryFragmentRenderPdf({url}: { url: string }) {
  return <iframe
    src={url}
    style={{width: "100%", height: "100%"}}/>;
}